import { Modal } from 'antd';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { GetEffectiveClientCashPercentage, RootState } from '@share/utils';
import { GetWalletAmount, IsCapitalVacationsAccount } from '@share/utils';

import './style.scss';

interface IProps {
  isOpen: boolean;
  clientCashName: string | null | undefined;
  currency: string | null | undefined;

  onClose: () => void;
}

interface IRow {
  key: string;
  label: string;
  hint: string;
  value: number | null | undefined;
  isHighlighted?: boolean;
}

export function WalletDetailsModal(props: IProps) {
  const { isOpen, clientCashName, currency, onClose } = props;

  const loginStore = useSelector((state: RootState) => state.loginStore);

  const { account, userWalletData } = loginStore;

  const rows: IRow[] = [
    {
      key: 'active',
      label: 'wallet.details.active',
      hint: 'wallet.details.active.hint',
      value: userWalletData?.balance,
      isHighlighted: true,
    },
    {
      key: 'pending',
      label: 'wallet.details.pending',
      hint: 'wallet.details.pending.hint',
      value: userWalletData?.pendingBalance,
    },
    {
      key: 'used',
      label: 'wallet.details.used',
      hint: 'wallet.details.used.hint',
      value: userWalletData?.usedBalance,
    },
    {
      key: 'expired',
      label: 'wallet.details.expired',
      hint: 'wallet.details.expired.hint',
      value: userWalletData?.expiredBalance,
    },
    {
      key: 'canceled',
      label: 'wallet.details.canceled',
      hint: 'wallet.details.canceled.hint',
      value: userWalletData?.canceledBalance,
    },
  ];

  // With no earning percentage configured (account-level or per-profile override) nothing can
  // ever accrue, so the pending bucket is meaningless — hide it instead of showing a balance the
  // member has no way to grow.
  const showPending = GetEffectiveClientCashPercentage(loginStore) > 0;

  const visibleRows = rows.filter(
    (row) => (row.key !== 'pending' || showPending) && (row.value || row.value === 0),
  );

  const isCapital = IsCapitalVacationsAccount(account);

  const convertionRate = userWalletData?.convertionRate;
  const showConvertionRate = !!convertionRate && convertionRate !== 1;

  return (
    <Modal
      className="wallet-details-modal"
      wrapClassName="wallet-details-modal-wrap"
      title={
        <FormattedMessage
          id={isCapital ? 'wallet.details.title.points' : 'wallet.details.title'}
          values={{ clientCash: clientCashName }}
        />}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      zIndex={5000}
      mask={false}
      getContainer={() => document.body}
    >
      <ul className="wallet-details-modal__rows">
        {visibleRows.map((row) => (
          <li
            key={row.key}
            className={`wallet-details-modal__row ${row.isHighlighted ? 'highlighted' : ''}`}
          >
            <div className="wallet-details-modal__row-head">
              <span className="wallet-details-modal__row-label">
                <FormattedMessage id={row.label} values={{ clientCash: clientCashName }} />
              </span>
              <span className="wallet-details-modal__row-value">
                {GetWalletAmount(row.value, account, userWalletData, clientCashName)}
              </span>
            </div>

            <p className="wallet-details-modal__row-hint">
              <FormattedMessage id={row.hint} values={{ clientCash: clientCashName }} />
            </p>
          </li>
        ))}
      </ul>

      {showConvertionRate ? (
        <p className="wallet-details-modal__rate">
          <FormattedMessage
            id="wallet.details.convertion_rate"
            values={{ clientCash: clientCashName, rate: convertionRate, currency }}
          />
        </p>) : null}
    </Modal>
  );
}
